import { MonitorPlay } from 'lucide-react';
import {
  MbSkeleton,
  type DetectedOrientation,
} from '@/features/campaigns/components/ManualBuilder';
import type { ScreenListItem, ScreenOrientationFilter } from '../ScreensStep.types';
import { ScreenCard } from './ScreenCard';

type ScreenResultsProps = {
  loading: boolean;
  screens: ScreenListItem[];
  selectedIds: string[];
  selectedCount: number;
  creativeOrientation?: DetectedOrientation;
  orientationFilter?: ScreenOrientationFilter;
  onSelect: (screen: Record<string, unknown>) => void;
  onResetOrientationFilter?: () => void;
};

function matchesCreative(
  creative: DetectedOrientation | undefined,
  screenOrientation?: string,
) {
  if (!creative || creative === 'UNKNOWN') return false;
  const screen = String(screenOrientation || '').toUpperCase();
  if (!screen) return false;
  if (creative === 'SQUARE') return screen === 'SQUARE';
  return screen === creative;
}

/** Responsive inventory grid keyed to content width (sidebar + optional preview aside). */
const GRID_CLASS =
  'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(220px,1fr))]';

export function ScreenResults({
  loading,
  screens,
  selectedIds,
  selectedCount,
  creativeOrientation,
  orientationFilter,
  onSelect,
  onResetOrientationFilter,
}: ScreenResultsProps) {
  const hideMatchBadge =
    orientationFilter === 'MATCHING' ||
    (Boolean(creativeOrientation) &&
      creativeOrientation !== 'UNKNOWN' &&
      orientationFilter === creativeOrientation);

  if (loading) {
    return (
      <div className={GRID_CLASS} aria-busy="true" aria-label="Loading screens">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="overflow-hidden rounded-xl border border-[#E6E8EF] bg-white"
          >
            <MbSkeleton className="h-[142px] w-full rounded-none" />
            <div className="space-y-1.5 px-3 py-2.5">
              <MbSkeleton className="h-3.5 w-[80%] rounded" />
              <MbSkeleton className="h-3 w-[55%] rounded" />
              <MbSkeleton className="mt-1.5 h-3 w-[40%] rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (screens.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-[16px] border border-dashed border-black/[0.1] bg-[#F7F8FA] px-5 py-12 text-center">
        <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(16,38,73,0.08)] ring-1 ring-black/[0.04]">
          <MonitorPlay className="h-5 w-5 text-[#9AA8B8]" />
        </span>
        <h3 className="m-0 text-[14px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
          No screens match
        </h3>
        <p className="mt-1 mb-0 max-w-sm text-[12.5px] leading-relaxed text-[var(--dash-muted)]">
          Try another search, clear location filters, or show all orientations
          in these cities.
        </p>
        {onResetOrientationFilter ? (
          <button
            type="button"
            onClick={onResetOrientationFilter}
            className="mt-4 rounded-full bg-[var(--dash-brand)] px-4 py-2 text-[12px] font-semibold text-white shadow-[0_4px_12px_-4px_rgba(41,121,255,0.5)] transition hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--dash-brand)]"
          >
            Show all screens
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={GRID_CLASS}>
      {screens.map((screen) => {
        const id = String(screen._id || screen.id || '');
        const selected = selectedIds.includes(id);
        const match = matchesCreative(
          creativeOrientation,
          screen.screenOrientation,
        );
        return (
          <ScreenCard
            key={id}
            screen={screen}
            selected={selected}
            selectedCount={selectedCount}
            recommended={match && !selected}
            hideMatchBadge={hideMatchBadge}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
}
